import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'astroturf/react';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import { humanReadableBytes } from 'js/social/shared/utils';
import { uploadImage } from 'js/social/api/image_api';
import { Image } from 'js/social/shared/types';
import { PostImage } from 'js/social/shared/PostImage';
import { AttachmentStatus, Banner, LoadingBanner } from './useAttachment';
import { AttachmentOverlay } from './AttachmentOverlay';
import { InfoIcon } from 'js/social/shared/Icons';

const MAX_FILE_SIZE = 10_000_000;

const ImageContainer = styled(AttachmentOverlay)`
  margin: 8px 0;
`;

export function useImageAttachment(initialImage?: Image, removable?: boolean) {
  const [status, setStatus] = useState(
    initialImage === undefined
      ? AttachmentStatus.Empty
      : AttachmentStatus.Ready,
  );
  const [error, setError] = useState<string>();
  const [image, setImage] = useState<Image | undefined>(initialImage);

  // React to changes in initialImage prop
  useEffect(() => {
    if (initialImage && !image) {
      setImage(initialImage);
      setStatus(AttachmentStatus.Ready);
      setError(undefined);
    }
  }, [initialImage, image]);

  const remove = useCallback(() => {
    setStatus(AttachmentStatus.Empty);
    setImage(undefined);
    setError(undefined);
  }, []);

  const attach = useCallback(async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setError(
        `This image is too large. Files must be ${humanReadableBytes(
          MAX_FILE_SIZE,
        )} or smaller.`,
      );
      setStatus(AttachmentStatus.Empty);
    } else {
      setError(undefined);
      setStatus(AttachmentStatus.Loading);
      try {
        setImage(await uploadImage(file));
        setStatus(AttachmentStatus.Ready);
      } catch (e) {
        // TS won't let us annotate the catch clause
        setError((e as Error).message);
        setStatus(AttachmentStatus.Empty);
      }
    }
  }, []);

  const content: ReactNode = useMemo(() => {
    if (status === AttachmentStatus.Loading) {
      return (
        <LoadingBanner icon={<Spinner />}>
          Your image is loading, it will be viewable shortly.
        </LoadingBanner>
      );
    } else if (status === AttachmentStatus.Ready && image) {
      return (
        <ImageContainer removable={removable} onRemove={remove}>
          <PostImage image={image} />
        </ImageContainer>
      );
    } else if (error) {
      return (
        <Banner type="warning" icon={<InfoIcon />}>
          {error}
        </Banner>
      );
    }
    return null;
  }, [status, image, error, remove, removable]);

  return { image, status, content, onAttach: attach };
}
